import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { object, mixed } from 'yup';
import { Alert, Button, Modal } from '@storable/canaveral-components';
import FormFieldFileUpload from '../FormFieldFileUpload';
import FormSubmitButton from '../FormSubmitButton';
import withForm from '../../hocs/withForm';

const readFile = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = err => reject(err);
  reader.onload = () => resolve(reader.result);
  reader.readAsText(file);
});

const propTypes = {
  certificate: PropTypes.shape({
    certContent: PropTypes.string.isRequired,
    keyContent: PropTypes.string.isRequired,
  }),
  dirty: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isValid: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  submitted: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  submitErrors: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
    }).isRequired,
  ),
  successMessage: PropTypes.string,
};

const defaultProps = {
  certificate: undefined,
  submitErrors: [],
  successMessage: undefined,
};

const HostnameCertSettingsStatusPanelUploadModal = ({
  certificate,
  dirty,
  handleSubmit,
  isValid,
  loading,
  onClose,
  submitted,
  submitting,
  submitErrors,
  successMessage,
}) => (
  <Modal
    aria-labelledby="manage-external-certificate-modal"
    onDismiss={onClose}
    show
    size="lg"
  >
    <form onSubmit={handleSubmit}>
      <Modal.Header>
        <Modal.Title>Manage External Certificate and Private Key</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {successMessage && (
          <Alert context="success">
            {successMessage}
          </Alert>
        )}
        {!submitting && submitErrors.length > 0 && (
          <Alert context="danger">
            {submitErrors.map(err => (
              <span key={err.code}>
                {err.message}&nbsp;
              </span>
            ))}
          </Alert>
        )}
        <FormFieldFileUpload
          accept=".crt,.pem"
          buttonText={certificate ? 'Replace SSL Certificate' : 'Upload SSL Certificate'}
          emptyFeedback={certificate ? null : 'No SSL Certificate has been uploaded'}
          fileKey="certFile"
          inlineFeedback
          label="SSL Certificate"
          name="certificate"
          required={!certificate}
        />
        <FormFieldFileUpload
          accept=".key,.pem"
          buttonText={certificate ? 'Replace Private Key' : 'Upload Private Key'}
          emptyFeedback={certificate ? null : 'No Private Key has been uploaded'}
          fileKey="keyFile"
          inlineFeedback
          label="Private Key"
          name="key"
          required={!certificate}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button disabled={loading} onClick={onClose}>
          {submitted ? 'Close' : 'Cancel'}
        </Button>
          <FormSubmitButton
            disabled={loading || !dirty || !isValid}
            submitting={submitting}
          />
      </Modal.Footer>
    </form>
  </Modal>
);

HostnameCertSettingsStatusPanelUploadModal.propTypes = propTypes;
HostnameCertSettingsStatusPanelUploadModal.defaultProps = defaultProps;

HostnameCertSettingsStatusPanelUploadModal.fragments = {
  certificate: gql`
    fragment HostnameCertSettingsStatusPanelUploadModalCertificate on Certificate {
      certContent
      keyContent
    }
  `,
  userError: gql`
    fragment HostnameCertSettingsStatusPanelUploadModalUserError on UserError {
      code
      message
    }
  `,
};

const initialValues = {
  certFile: undefined,
  keyFile: undefined,
};

const validationSchema = object().shape({
  certFile: mixed(),
  keyFile: mixed(),
});

export default withForm({
  mapPropsToValues: () => initialValues,
  validationSchema,
  handleSubmit: async (values, formikBag) => {
    const { certFile, keyFile } = values;
    const { certificate, onSave } = formikBag.props;
    const { certContent, keyContent } = certificate || {};

    const certValue = certFile ? await readFile(certFile) : certContent;
    const keyValue = keyFile ? await readFile(keyFile) : keyContent;

    onSave(certValue, keyValue);
  },
})(HostnameCertSettingsStatusPanelUploadModal);
